import { addNewLineItem } from "@components/smart/smartFastEntryList";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext, { IEntity } from "@odata/BindingContext";
import {
    ICollectiveAgreementTypeEntity,
    IPrJmhzSubmissionEntity,
    PrJmhzAnnualOverviewEntity,
    PrJmhzDetailEntity,
    PrJmhzEmployeeAnnualTaxSettlementEntity,
    PrJmhzEmployeeEntity,
    PrJmhzEmployeeIncomeOverviewEntity,
    PrJmhzEmployeeIncomeTaxOverviewEntity,
    PrJmhzEmploymentEntity,
    PrJmhzEmploymentIncomeOverviewEntity,
    PrJmhzSubmissionEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { PrSubmissionGroup } from "@pages/payroll/submission/Submission.def";
import { setCompanyRelation } from "@pages/payroll/submission/Submission.utils";
import { SubmissionsFormViewForExtend } from "@pages/payroll/submission/SubmissionFormView";
import {
    _annualOverviewPath,
    _employeeIncomeOverviewPath,
    _employmentIncomeOverviewPath
} from "@pages/payroll/submission/submissionTypes/PrJmhzSubmission.def";
import { ifTruthy, isInMonths } from "@pages/payroll/submission/submissionTypes/PrJmhzSubmission.utils";
import { searchAddress } from "@utils/address.utils";
import { isDefined, isObjectEmpty } from "@utils/general";

import { withPermissionContext } from "../../../../contexts/permissionContext/withPermissionContext";
import { Status } from "../../../../enums";
import { FormStorage, IFormAlert } from "../../../../views/formView/FormStorage";

class PrJmhzSubmissionFormView extends SubmissionsFormViewForExtend<IPrJmhzSubmissionEntity> {
    typeCode = PrSubmissionTypeCode.MonthlyReport;
    subTypeCode: PrSubmissionSubtypeCode = null;
    pathToEmployee: string = null;

    typeSpecificOnAfterLoad(isNew: boolean): Promise<void> {
        const { storage } = this.props;

        const entity = storage.getEntity();
        const promises: Promise<void>[] = [];

        if (isNew) {
            promises.push(setCompanyRelation(storage as unknown as FormStorage, { socialFields: true }));
            promises.push(this.massDefaultCityCode());
        }
        this.initCollectiveAgreementType();
        // set local context for showing/hiding fields
        return Promise.all([...promises, super.typeSpecificOnAfterLoad(isNew)]).then();
    }

    getSubmissionWarningMessage(): IFormAlert {
        const submissionValidationWarning = super.getSubmissionWarningMessage();
        if (submissionValidationWarning) {
            return submissionValidationWarning;
        }

        const { storage } = this.props;
        const isDecemberSubmission = isInMonths([12])({ storage });

        const annualOverview = storage.data.entity.Detail.AnnualOverview;
        // if user fills at least one of the statistical fields, we do not show warning as user is aware of the fields and fills them
        const hasSomeFieldDefined = isDefined(annualOverview.DisabledEmployeeShare)
            || isDefined(annualOverview.AverageAnnualEmployeeCount) || isDefined(annualOverview.AverageAnnualDisabledEmployeeCount);

        if (isDecemberSubmission && !hasSomeFieldDefined) {
            return {
                title: storage.t("Submission:JmhzForm.StatisticalFieldsNotFilledTitle"),
                subTitle: storage.t("Submission:JmhzForm.StatisticalFieldsNotFilledMessage"),
                status: Status.Warning
            };
        }

        return null;
    }

    async massDefaultCityCode() {
        const { storage } = this.props;
        const cities = new Map<string, string>();
        storage.data.entity.Detail?.Employees?.forEach(employee => {
            employee?.Employments?.forEach(employment => {
                if (employment?.City && !employment?.CityCode) {
                    cities.set(employment.City, null);
                }
            });
        });
        const cityList = Array.from(cities.keys());
        const promises = cityList.map(city => searchAddress(city));
        const results = await Promise.all(promises);
        results.forEach((data, index) => {
            const city = cityList[index];
            const exactMatch = data?.filter(d => d.City === city);
            if (exactMatch?.length === 1) {
                cities.set(city, exactMatch[0].AddressCode);
            }
        });
        const bc = storage.data.bindingContext
            .navigate(PrJmhzSubmissionEntity.Detail)
            .navigate(PrJmhzDetailEntity.Employees);
        storage.data.entity.Detail?.Employees?.forEach(employee => {
            const employmentsBc = bc.addKey(employee).navigate(PrJmhzEmployeeEntity.Employments);
            employee?.Employments?.forEach(employment => {
                const cityCodeBc = employmentsBc.addKey(employment).navigate(PrJmhzEmploymentEntity.CityCode);
                if (employment?.City && !employment?.CityCode) {
                    const addressCode = cities.get(employment.City);
                    if (addressCode) {
                        storage.setValue(cityCodeBc, addressCode);
                    }
                }
            });
        });
    }

    handleChange(e: ISmartFieldChange): void {
        const { storage } = this.props;

        super.handleChange(e);
        // keep after storage -> handleChange to have updated data

        this.handleHasOtherSupportingPersonChange(e);
    }

    async handleBlur(args: ISmartFieldBlur): Promise<void> {
        this.calculateNonTaxableIncomeTotal(args);
        this.calculateSupplementsTotal(args);

        return super.handleBlur(args);
    }

    calculateNonTaxableIncomeTotal(e: ISmartFieldBlur): void {
        const { storage } = this.props;
        const path = e.bindingContext.getNavigationPath(true);

        if ([
            _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.EmployerRetirementSavingsContributionTotal),
            _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.LongTermCareInsuranceContribution)
        ].includes(path)) {
            const parentBc = e.bindingContext.getParent();

            const nonTaxableIncomeFields = [
                PrJmhzEmployeeIncomeOverviewEntity.EmployerRetirementSavingsContributionTotal,
                PrJmhzEmployeeIncomeOverviewEntity.LongTermCareInsuranceContribution
            ];

            const total = nonTaxableIncomeFields.reduce((sum, field) => {
                const value = storage.getValue(parentBc.navigate(field));
                return sum + (Number.isNaN(value) ? 0 : Number(value));
            }, 0);

            storage.setValue(parentBc.navigate(PrJmhzEmployeeIncomeOverviewEntity.NonTaxableIncomeTotal), total);
        }
    }

    calculateSupplementsTotal(e: ISmartFieldBlur): void {
        const { storage } = this.props;
        const path = e.bindingContext.getNavigationPath(true);

        const fields = [
            PrJmhzEmploymentIncomeOverviewEntity.OvertimeSupplement,
            PrJmhzEmploymentIncomeOverviewEntity.NightWorkSupplement,
            PrJmhzEmploymentIncomeOverviewEntity.WeekendWorkSupplement,
            PrJmhzEmploymentIncomeOverviewEntity.HolidayWorkSupplement
        ];

        if (fields.map(f => _employmentIncomeOverviewPath(f)).includes(path)) {
            const parentBc = e.bindingContext.getParent();

            const total = fields.reduce((sum, field) => {
                const value = storage.getValue(parentBc.navigate(field));
                return sum + (Number.isNaN(value) ? 0 : Number(value));
            }, 0);

            storage.setValue(parentBc.navigate(PrJmhzEmploymentIncomeOverviewEntity.SupplementsTotal), total);
        }
    }

    handleHasOtherSupportingPersonChange(e: ISmartFieldChange): void {
        const { storage } = this.props;
        const path = e.bindingContext.getPath();

        // incomeTax or AnnualTax -> SupportingPersons
        const supportedIds = [
            PrJmhzEmployeeIncomeTaxOverviewEntity.HasOtherSupportingPerson,
            PrJmhzEmployeeAnnualTaxSettlementEntity.HasSpouseDiscount,
            PrJmhzEmployeeAnnualTaxSettlementEntity.HasChildTaxBenefit
        ] as string[];
        if (supportedIds.includes(path) && e.triggerAdditionalTasks) {
            const parentBc = e.bindingContext.getParent();
            let lineItemsBc: BindingContext;
            let groupId: string = null;
            switch (parentBc.getPath()) {
                case PrJmhzEmployeeEntity.AnnualTaxSettlement:
                    if (path === PrJmhzEmployeeAnnualTaxSettlementEntity.HasOtherSupportingPerson) {
                        groupId = PrSubmissionGroup.AnnualTaxSettlement_2;
                        lineItemsBc = parentBc.navigate(PrJmhzEmployeeAnnualTaxSettlementEntity.SupportingPersons);
                    } else if (path === PrJmhzEmployeeAnnualTaxSettlementEntity.HasSpouseDiscount) {
                        groupId = PrSubmissionGroup.AnnualTaxSettlement;
                        lineItemsBc = parentBc.navigate(PrJmhzEmployeeAnnualTaxSettlementEntity.Partners);
                    } else if (path === PrJmhzEmployeeAnnualTaxSettlementEntity.HasChildTaxBenefit) {
                        groupId = PrSubmissionGroup.AnnualTaxSettlement_3;
                        lineItemsBc = parentBc.navigate(PrJmhzEmployeeAnnualTaxSettlementEntity.SupportedChildren);
                    }
                    break;
                case PrJmhzEmployeeEntity.IncomeTaxOverview:
                    groupId = PrSubmissionGroup.SupportingPersons;
                    lineItemsBc = parentBc.navigate(PrJmhzEmployeeIncomeTaxOverviewEntity.SupportingPersons);
            }
            if (ifTruthy(() => e.bindingContext)({ storage })) {
                const currentValue = storage.getValue(lineItemsBc) as IEntity[];
                if (currentValue?.length) {
                    // already has items, do not add new one
                    return;
                }
                // add item, refresh group -> makes add button visible
                if (groupId) {
                    const def = storage.data.definition.groups.find(g => g.id === groupId);
                    addNewLineItem(storage, lineItemsBc, def);
                }
            } else {
                storage.setValue(lineItemsBc, []);
            }
            storage.refresh(true);
        }
    }

    initCollectiveAgreementType(): void {
        const { storage } = this.props;
        const { bindingContext } = storage.data;
        const typesBc = bindingContext.navigate(_annualOverviewPath(PrJmhzAnnualOverviewEntity.CollectiveAgreementTypes));
        const types = storage.getValue(typesBc) as ICollectiveAgreementTypeEntity[];
        if (!types?.length && isInMonths([12])({ storage })) {
            // add first item to the collection
        }
    }

    onBeforeSave(data?: IPrJmhzSubmissionEntity): IPrJmhzSubmissionEntity {
        data = super.onBeforeSave(data);

        data.Detail?.Employees?.forEach((employee, idx) => {
            if (isObjectEmpty(employee.WithholdingTaxOverview)) {
                // if no properties are set, remove the whole object as some of it props are mandatory if present
                data.Detail.Employees[idx].WithholdingTaxOverview = null;
            }
        });

        return data;
    }
}

export default withPermissionContext(PrJmhzSubmissionFormView);
